import { Dictionary } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { fetchOrganizationUnits, useMyUnits } from '~/data/orgunits';
import { useSWR } from '~/hooks/useSWR';
import { ChildInput } from '~/legacy-components/TopbarSurvey/types';
import { StoreState } from '~/reducer';
import { orgUnitsStoreStateSelector } from '~/selectors';
import { IndicatorByUnitResponse } from '~/services/Http';
import { AppDispatch } from '~/store';

import {
  GET_INDICATORS_BY_UNIT_SWR_KEY,
  getIndicatorsByUnit,
} from '../../data';

export interface UseContentOutput {
  loading: boolean;
  failedToLoad: boolean;
  indicatorDictionary?: Dictionary<IndicatorByUnitResponse[]>;
  unitList: ChildInput[];
  selectedUnitId: number | undefined;
  changeSelectedUnit: (id: number) => void;
}

export const useContent = (): UseContentOutput => {
  const { isLoading, failedToLoad } = useSelector((state: StoreState) => {
    return orgUnitsStoreStateSelector(state);
  });
  const dispatch: AppDispatch = useDispatch();
  const { myUnits } = useMyUnits();
  const myRoles = useLoggedInUserRoles();
  const myId = useLoggedInEmployeeId();

  const unitList = myUnits.map((unit) => {
    return {
      value: unit.fOrgUnitId,
      label: unit.fOrgUnitName,
    };
  }) as ChildInput[];

  const [selectedUnitId, setSelectedUnitId] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    if (myUnits[0] && !selectedUnitId) {
      setSelectedUnitId(myUnits[0].fOrgUnitId);
    }
  }, [myUnits, selectedUnitId]);

  useEffect(() => {
    dispatch(fetchOrganizationUnits({ myRoles, myId }));
  }, [myRoles, myId, dispatch]);

  const changeSelectedUnit = useCallback(
    (id: number) => {
      setSelectedUnitId(id);
    },
    [setSelectedUnitId],
  );

  const {
    failedToLoad: failedToLoadIndicatorDictionary,
    loading: loadingIndicatorDictionary,
    data: indicatorDictionary,
  } = useSWR(
    selectedUnitId ? [GET_INDICATORS_BY_UNIT_SWR_KEY, selectedUnitId] : null,
    ([url, selectedUnitId]) => getIndicatorsByUnit(selectedUnitId),
  );

  return {
    failedToLoad: failedToLoad || failedToLoadIndicatorDictionary,
    loading: isLoading || loadingIndicatorDictionary,
    indicatorDictionary,
    unitList,
    selectedUnitId,
    changeSelectedUnit,
  };
};
